"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const styled_components_1 = __importDefault(require("styled-components"));
const ui_1 = require("@completeanatomy/ui");
const bridge_1 = require("@completeanatomy/bridge");
const geometry_1 = require("../../utils/geometry");
const bridge_schema_1 = require("@completeanatomy/bridge-schema");
const fracture_1 = require("./fracture");
const DEFAULT_SPHERE_DIAMETER = 50;
// eslint-disable-next-line max-lines-per-function
function ToolsFractureLine({ width = 0, height = 0, color, size = 0, }) {
    const [drawState, setDrawState] = (0, react_1.useState)({
        isDrawing: false,
    });
    const [points, setPoints] = (0, react_1.useState)(fracture_1.coordinates);
    const facturePoints = (0, react_1.useRef)([]);
    const canvasRef = (0, react_1.useRef)(null);
    const bridge = (0, bridge_1.useBridge)();
    const onPaintStartHandler = (0, react_1.useCallback)((event) => {
        // prevent when clicking on buttons
        if ((event.target && event.target.tagName === 'BUTTON') ||
            event.target.closest('button')) {
            return;
        }
        if (drawState.isDrawing || (0, ui_1.isMultiTouchEvent)(event)) {
            return;
        }
        const point = (0, ui_1.getPosition)(event);
        setDrawState({
            isDrawing: true,
            startPoint: point,
            endPoint: undefined,
        });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawState]);
    const onPaintHandler = (0, react_1.useCallback)((event) => {
        // prevent when clicking on buttons
        if ((event.target && event.target.tagName === 'BUTTON') ||
            event.target.closest('button')) {
            return;
        }
        if (drawState.isDrawing && !(0, ui_1.isMultiTouchEvent)(event)) {
            const point = (0, ui_1.getPosition)(event);
            setDrawState(Object.assign(Object.assign({}, drawState), { endPoint: point }));
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawState]);
    const onPaintEndHandler = (0, react_1.useCallback)((event) => {
        // prevent when clicking on buttons
        if ((event.target && event.target.tagName === 'BUTTON') ||
            event.target.closest('button')) {
            return;
        }
        if (!drawState.isDrawing) {
            return;
        }
        setDrawState(Object.assign(Object.assign({}, drawState), { isDrawing: false }));
        if (facturePoints.current.length > 0) {
            bridge === null || bridge === void 0 ? void 0 : bridge.fractureModel({ points: facturePoints.current });
            const shapeCoords = facturePoints.current.map(point => [point.x, point.y]);
            bridge === null || bridge === void 0 ? void 0 : bridge.sendFractureShape({
                fractureType: bridge_schema_1.ToolType.FractureSimple,
                shapeCoords: shapeCoords,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawState, bridge]);
    (0, react_1.useEffect)(() => {
        if (!canvasRef.current) {
            return;
        }
        const events = (0, ui_1.getPointersEvents)();
        document.addEventListener(events.down, onPaintStartHandler);
        document.addEventListener(events.move, onPaintHandler);
        document.addEventListener(events.up, onPaintEndHandler);
        return () => {
            document.removeEventListener(events.down, onPaintStartHandler);
            document.removeEventListener(events.move, onPaintHandler);
            document.removeEventListener(events.up, onPaintEndHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onPaintStartHandler, onPaintHandler, onPaintEndHandler]);
    (0, react_1.useEffect)(() => {
        const values = fracture_1.coordinates.map(point => point.x);
        const max = Math.max(...values);
        let first = true;
        const scaledCoordinates = fracture_1.coordinates.map(point => {
            if (point.x == max && first) {
                first = false;
            }
            return first
                ? { x: point.x, y: point.y - size }
                : { x: point.x, y: point.y + size };
        });
        setPoints(scaledCoordinates);
    }, [size]);
    (0, react_1.useEffect)(() => {
        if (!canvasRef.current) {
            return;
        }
        const context = canvasRef.current.getContext('2d');
        if (!context) {
            return;
        }
        context.clearRect(0, 0, width, height);
        if (drawState.isDrawing && drawState.startPoint && drawState.endPoint) {
            const delta = (0, ui_1.getDelta)(drawState.startPoint, drawState.endPoint);
            const rotateAngle = Math.atan2(delta.y, delta.x);
            const distance = (0, ui_1.getDistance)(drawState.startPoint, drawState.endPoint);
            const origin = (0, ui_1.getMidPointBetween)(drawState.startPoint, drawState.endPoint);
            const sphereScale = distance / DEFAULT_SPHERE_DIAMETER;
            const sphereRadius = (DEFAULT_SPHERE_DIAMETER / 2.0) * sphereScale;
            const leftTopCorner = {
                x: origin.x - sphereRadius,
                y: origin.y - sphereRadius,
            };
            const rotateOrigin = {
                x: DEFAULT_SPHERE_DIAMETER / 2,
                y: DEFAULT_SPHERE_DIAMETER / 2,
            };
            facturePoints.current = [];
            for (let i = 0; i < points.length; i++) {
                const point = { x: points[i].x, y: points[i].y };
                const newPoint = (0, geometry_1.rotatePoint)(point, rotateOrigin, rotateAngle - Math.PI);
                facturePoints.current.push({
                    x: leftTopCorner.x + newPoint.x * sphereScale,
                    y: leftTopCorner.y + newPoint.y * sphereScale,
                });
            }
            context.beginPath();
            context.moveTo(facturePoints.current[0].x, facturePoints.current[0].y);
            for (let i = 1; i < facturePoints.current.length; i++) {
                context.lineTo(facturePoints.current[i].x, facturePoints.current[i].y);
            }
            context.closePath();
            context.fillStyle = color !== null && color !== void 0 ? color : '';
            context.fill();
        }
    }, [width, height, color, drawState, points]);
    return (0, jsx_runtime_1.jsx)(Canvas, { ref: canvasRef, width: width, height: height, role: "main", "data-e2e": 'ToolsFractureLine_Canvas' });
}
exports.default = ToolsFractureLine;
const Canvas = styled_components_1.default.canvas `
  position: absolute;
  top: 0;
  left: 0;

  user-select: none;
`;
